/* eslint-disable max-lines */
import "@fontsource/manrope/300.css";
import "@fontsource/manrope/400.css";
import "@fontsource/manrope/500.css";
import "@fontsource/manrope/600.css";
import "@fontsource/manrope/700.css";
import "@fontsource/outfit/400.css";
import "@fontsource/outfit/500.css";
import "@fontsource/outfit/600.css";
import "@fontsource/outfit/700.css";
import { color } from "@lumi/tokens";
import "mapbox-gl/dist/mapbox-gl.css"; // Required for Mapbox GL JS styling
import React from "react";
import ReactDOM from "react-dom/client";
import { createHashRouter, RouterProvider } from "react-router-dom";
import { AppShell } from "./app/AppShell";
import { AuthProvider } from "./app/auth/AuthContext";
import { ProtectedRoute, RoleProtectedRoute } from "./app/auth/ProtectedRoute";
import { browseLoader } from "./app/loaders/browseLoader";
import { AdminDashboard } from "./app/pages/admin/AdminDashboard";
import { AdminLayout } from "./app/pages/admin/AdminLayout";
import { AnalyticsPage } from "./app/pages/admin/AnalyticsPage";
import { AuditLogPage } from "./app/pages/admin/AuditLog";
import { BulkEditTitles } from "./app/pages/admin/bulk-edit/BulkEditTitles";
import { CollectionsPage } from "./app/pages/admin/CollectionsPage";
import { EditTitle } from "./app/pages/admin/EditTitle";
import { EncodingDashboard } from "./app/pages/admin/EncodingDashboard";
import { ProviderDetailPage } from "./app/pages/admin/ProviderDetailPage";
import { ProvidersPage } from "./app/pages/admin/ProvidersPage";
import { ReviewQueue } from "./app/pages/admin/ReviewQueue";
import { ReviewSubmissionPage } from "./app/pages/admin/ReviewSubmissionPage";
import { TitlesLibrary } from "./app/pages/admin/TitlesLibrary";
import { ForgotPassword } from "./app/pages/auth/ForgotPassword";
import { ResetPassword } from "./app/pages/auth/ResetPassword";
import { SignInPage } from "./app/pages/auth/SignIn";
import { SignUpPage } from "./app/pages/auth/SignUp";
import { SplashPage } from "./app/pages/auth/Splash";
import { GroupedBrowsePage } from "./app/pages/browse/GroupedBrowsePage";
import { Help } from "./app/pages/help/Help";
import { HomePage } from "./app/pages/home/Home";
import { PrivacyPolicy } from "./app/pages/legal/PrivacyPolicy";
import { TermsOfService } from "./app/pages/legal/TermsOfService";
import { PlayerPage } from "./app/pages/player/Player";
import { CreateSubmission } from "./app/pages/provider/pages/CreateSubmission";
import { EditSubmission } from "./app/pages/provider/pages/EditSubmission";
import { ProviderAnalyticsPage } from "./app/pages/provider/pages/ProviderAnalyticsPage";
import { ProviderAuditLog } from "./app/pages/provider/pages/ProviderAuditLog";
import { ProviderDashboard } from "./app/pages/provider/pages/ProviderDashboard";
import { ProviderProfileSettings } from "./app/pages/provider/pages/ProviderProfileSettings";
import { SubmissionsDashboard } from "./app/pages/provider/pages/SubmissionsDashboard";
import { ViewSubmission } from "./app/pages/provider/pages/ViewSubmission";
import { ProviderLayout } from "./app/pages/provider/ProviderLayout";
import { ProviderProfilePage } from "./app/pages/providers/ProviderProfilePage";
import { SearchResults } from "./app/pages/search/SearchResults";
import { TitlePage } from "./app/pages/title/Title";
import { Account } from "./app/pages/user/Account";
import { AppSettings } from "./app/pages/user/AppSettings";
import { CompleteStep } from "./app/pages/user/CompleteStep";
import { PreferencesStep } from "./app/pages/user/PreferencesStep";
import { Profiles } from "./app/pages/user/Profiles";
import { Dashboard } from "./app/pages/user/UserDashboard";
import { UserLayout } from "./app/pages/user/UserLayout";
import { WelcomeStep } from "./app/pages/user/WelcomeStep";
import { RootLayout } from "./app/RootLayout";
import { KeyboardNavigationWrapper } from "./app/shared/components/KeyboardNavigationWrapper";
import { TelemetryProvider } from "./app/telemetry/TelemetryContext";
import { initSentry } from "./sentry";
import "./styles/tailwind.css";
import { registerWebAdapters } from "./ui-adapters";

// Initialize Sentry error tracking
initSentry();

// Register web adapters once at startup
registerWebAdapters();

const style = { "--lumi-accent": color.accent } as React.CSSProperties & { "--lumi-accent": string };

const router = createHashRouter([
  {
    path: "/",
    element: <RootLayout />,
    children: [
      {
        index: true,
        element: <SplashPage />,
      },
      {
        path: "sign-in",
        element: <SignInPage />,
      },
      {
        path: "sign-up",
        element: <SignUpPage />,
      },
      {
        path: "forgot-password",
        element: <ForgotPassword />,
      },
      {
        path: "reset-password",
        element: <ResetPassword />,
      },
      {
        path: "legal/terms",
        element: <TermsOfService />,
      },
      {
        path: "legal/privacy",
        element: <PrivacyPolicy />,
      },
      {
        // Onboarding flow for new users
        path: "user",
        element: <UserLayout />,
        children: [
          {
            path: "welcome",
            element: <WelcomeStep />,
          },
          {
            path: "preferences",
            element: <PreferencesStep />,
          },
          {
            path: "complete",
            element: <CompleteStep />,
          },
          {
            path: "dashboard",
            element: <Dashboard />,
          },
          {
            path: "account",
            element: <Account />,
          },
          {
            path: "profiles",
            element: <Profiles />,
          },
          {
            path: "settings",
            element: <AppSettings />,
          },
        ],
      },
      {
        // Wrap all routes with KeyboardNavigationProvider (needs access to useLocation)
        element: <KeyboardNavigationWrapper />,
        children: [
          {
            path: "help",
            element: <Help />,
          },
          {
            // Protected routes - require authentication
            element: <ProtectedRoute />,
            children: [
              {
                path: "home",
                element: <AppShell />,
                children: [
                  {
                    index: true,
                    element: <HomePage />,
                  },
                  { path: "title/:id", element: <TitlePage /> },
                  { path: "providers/:slug", element: <ProviderProfilePage /> },
                  { path: "watch/:id", element: <PlayerPage /> },
                  {
                    path: "browse",
                    element: <GroupedBrowsePage />,
                    loader: browseLoader,
                  },
                  { path: "search", element: <SearchResults /> },
                ],
              },
              {
                // Admin routes - require ADMIN role
                path: "admin",
                element: <RoleProtectedRoute allowedRoles={["ADMIN"]} />,
                children: [
                  {
                    element: <AdminLayout />,
                    children: [
                      {
                        index: true,
                        element: <AdminDashboard />,
                      },
                      {
                        path: "review-queue",
                        element: <ReviewQueue />,
                      },
                      {
                        path: "review/:id",
                        element: <ReviewSubmissionPage />,
                      },
                      {
                        path: "providers",
                        element: <ProvidersPage />,
                      },
                      {
                        path: "providers/:id",
                        element: <ProviderDetailPage />,
                      },
                      {
                        path: "collections",
                        element: <CollectionsPage />,
                      },
                      {
                        path: "titles",
                        element: <TitlesLibrary />,
                      },
                      {
                        path: "titles/bulk-edit",
                        element: <BulkEditTitles />,
                      },
                      {
                        path: "titles/:id/edit",
                        element: <EditTitle />,
                      },
                      {
                        path: "audit-log",
                        element: <AuditLogPage />,
                      },
                      {
                        path: "analytics",
                        element: <AnalyticsPage />,
                      },
                      {
                        path: "encoding-queue",
                        element: <EncodingDashboard />,
                      },
                    ],
                  },
                ],
              },
              {
                // Provider routes - require CONTENT_PROVIDER or ADMIN role
                path: "provider",
                element: <RoleProtectedRoute allowedRoles={["CONTENT_PROVIDER", "ADMIN"]} />,
                children: [
                  {
                    element: <ProviderLayout />,
                    children: [
                      {
                        index: true,
                        element: <ProviderDashboard />,
                      },
                      {
                        path: "submissions",
                        element: <SubmissionsDashboard />,
                      },
                      {
                        path: "analytics",
                        element: <ProviderAnalyticsPage />,
                      },
                      {
                        path: "audit-log",
                        element: <ProviderAuditLog />,
                      },
                      {
                        path: "profile",
                        element: <ProviderProfileSettings />,
                      },
                      {
                        path: "new",
                        element: <CreateSubmission />,
                      },
                      {
                        path: "edit/:id",
                        element: <EditSubmission />,
                      },
                      {
                        path: "view/:id",
                        element: <ViewSubmission />,
                      },
                    ],
                  },
                ],
              },
            ], // End ProtectedRoute children
          },
        ], // End KeyboardNavigationWrapper children
      },
    ], // End RootLayout children
  },
]);

// Wait for API server to be ready in development to prevent CORS timing issues
const startApp = async () => {
  if (import.meta.env.MODE === "development") {
    const { waitForApiReady } = await import("./app/utils/api-health");
    const apiUrl = import.meta.env.VITE_API_BASE_URL || "http://localhost:3333";
    await waitForApiReady(apiUrl);
  }

  ReactDOM.createRoot(document.getElementById("root")!).render(
    <React.StrictMode>
      <AuthProvider>
        <TelemetryProvider>
          <div className="font-[Inter,system-ui,ui-sans-serif]" style={style}>
            <RouterProvider router={router} />
          </div>
        </TelemetryProvider>
      </AuthProvider>
    </React.StrictMode>,
  );
};

// Start the app
await startApp();

// Register service worker for PWA support
if ("serviceWorker" in navigator && import.meta.env.PROD) {
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/sw.js")
      .then((registration) => {
        console.log("Service Worker registered:", registration);
      })
      .catch((error) => {
        console.error("Service Worker registration failed:", error);
      });
  });
}
